import { useState, useEffect, useCallback, useRef } from 'react'

export interface JobEvent {
  event: string
  document_id?: string
  document_name?: string
  status?: string
  fields_extracted?: number
  tokens_used?: number
  progress?: { done: number; total: number }
  error?: string
  timestamp?: string
  job_id?: string
}

export function useProcessingJob(jobId: string | null) {
  const [events, setEvents] = useState<JobEvent[]>([])
  const [isComplete, setIsComplete] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Always poll job status — this is the reliable source of truth
  useEffect(() => {
    if (!jobId || isComplete) return
    const token = localStorage.getItem('access_token')

    const poll = async () => {
      try {
        const res = await fetch(`/api/extraction/jobs/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) return
        const job = await res.json()
        const done = (job.processed_documents || 0) + (job.failed_documents || 0)
        setProgress({ done, total: job.total_documents })

        if (job.status === 'completed' || job.status === 'failed') {
          setIsComplete(true)
        }
      } catch {
        // ignore
      }
    }

    // Poll immediately, then every 2s
    poll()
    pollRef.current = setInterval(poll, 2000)

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [jobId, isComplete])

  // WebSocket for real-time events (document names, field counts)
  useEffect(() => {
    if (!jobId || isComplete) return

    const token = localStorage.getItem('access_token')
    const wsUrl = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws/jobs/${jobId}?token=${token}`
    const ws = new WebSocket(wsUrl)

    ws.onopen = () => setIsConnected(true)

    ws.onmessage = (evt) => {
      try {
        const data: JobEvent = JSON.parse(evt.data)
        // Only add document-level events (progress is handled by polling)
        if (data.event === 'document_processed' || data.event === 'document_failed') {
          setEvents((prev) => [...prev, data])
        }
        if (data.event === 'job_completed' || data.event === 'job_failed') {
          setIsComplete(true)
        }
      } catch {
        // ignore
      }
    }

    ws.onerror = () => setIsConnected(false)
    ws.onclose = () => setIsConnected(false)

    return () => ws.close()
  }, [jobId, isComplete])

  // Stop polling when complete
  useEffect(() => {
    if (isComplete && pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [isComplete])

  const reset = useCallback(() => {
    setEvents([])
    setIsComplete(false)
    setIsConnected(false)
    setProgress(null)
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  return { events, isComplete, isConnected, progress, reset }
}
